import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUserDetails, AddUsers, AddEvt_Organiser,
  AddEvt_UserOrganiserMapping, getOrganisersByUser,
} from '../../api/api';
import { passwordProblem } from '../../utils/authFlow';

// Self-onboarding: user → organiser (owned by that user) → owner mapping → sign in.
export function useRegister() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: '', surname: '', mobile: '', password: '', confirm: '', organiser: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => {
    const val = k === 'mobile' ? String(v || '').replace(/\D/g, '').slice(0, 10) : v;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    const { name, surname, mobile, password, confirm, organiser } = form;
    if (!name.trim()) { setErr('Enter your name'); return; }
    if (!/^\d{10}$/.test(mobile)) { setErr('Enter a valid 10-digit mobile number'); return; }
    if (!password) { setErr('Enter a password'); return; }
    { const problem = passwordProblem(password, mobile); if (problem) { setErr(problem); return; } }
    if (password !== confirm) { setErr('Passwords do not match'); return; }
    if (!organiser.trim()) { setErr('Enter your organiser / business name'); return; }

    setBusy(true);
    try {
      // 1) Reject if the mobile is already registered.
      const existing = await getUserDetails({ MobileNumber: mobile });
      if (Array.isArray(existing) && existing.length > 0) {
        setErr('This mobile number is already registered. Please sign in.');
        return;
      }
      // 2) Create the user.
      const userRes = await AddUsers({
        MobileNumber: mobile, Password: password, Name: name, Surname: surname, Valid: 1,
      });
      const userId = userRes && userRes.insertId;
      if (!userId) { setErr('Could not create the user account.'); return; }
      // 3) Create the organiser (tenant), owned by this user.
      const orgRes = await AddEvt_Organiser({
        OrganiserName: organiser, OrganiserMasterUser: userId,
        MobileNumber: mobile, Valid: 1, CreatedBy: userId,
      });
      const organiserId = orgRes && orgRes.insertId;
      if (!organiserId) { setErr('Account created, but the organiser could not be set up.'); return; }
      // 4) Link user ↔ organiser as its Owner (IsAdmin=1 → full access; login requires this mapping).
      await AddEvt_UserOrganiserMapping({
        UserId: userId, OrganiserId: organiserId, IsAdmin: 1, Valid: 1, CreatedBy: userId,
      });
      // 5) Auto sign-in (the next launch goes through Login → verifyDevice and binds the device).
      const users = await getUserDetails({ MobileNumber: mobile });
      const user = Array.isArray(users) ? users[0] : null;
      const organisers = await getOrganisersByUser({ UserId: userId });
      const list = Array.isArray(organisers) ? organisers : [];
      if (user && list.length > 0) {
        login(user, list[0], { mobile, password }, true);
        nav('/app/dashboard');
      } else {
        nav('/', { state: { registered: true } });
      }
    } catch {
      setErr('Registration failed. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return { form, set, busy, err, submit };
}
